import { Button } from '@ophelos/ui/components/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@ophelos/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { Check, ChevronsUpDown, Globe, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useHub } from '@/contexts/HubContext';
import { GlobalCellSwitcher } from '@/lib/global/GlobalCellSwitcher';
import { isGlobalSurface } from '@/lib/surface-mode';
import { cn } from '@/lib/utils';
import type { Operator } from '@/types';

export function OperatorSwitcher({ size = 'default' }: { size?: 'sm' | 'default' }) {
  if (isGlobalSurface()) return <GlobalCellSwitcher size={size} />;
  return <LocalOperatorSwitcher size={size} />;
}

function LocalOperatorSwitcher({ size = 'default' }: { size?: 'sm' | 'default' }) {
  const { operators, selectedOperator, setSelectedOperator, selectedClient } = useHub();
  const [open, setOpen] = React.useState(false);
  const [switching, setSwitching] = React.useState(false);

  if (!selectedOperator) return null;

  const handleOperatorSwitch = async (operator: Operator) => {
    if (!operator.workos_organization_id) {
      console.error('Operator does not have a WorkOS organization ID');
      return;
    }

    setSwitching(true);
    try {
      await setSelectedOperator(operator);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch organization:', error);
      // The WorkOS SDK will handle redirecting to login if SSO/MFA is required
    } finally {
      setSwitching(false);
    }
  };

  const isOperatorView = !selectedClient;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={size === 'sm' ? 'xs' : 'default'}
          className={` ${isOperatorView ? 'bg-accent/50 hover:bg-accent/75 border border-ring/25 hover:border-ring/50 text-accent-foreground dark:bg-accent dark:hover:bg-accent/75 dark:border-ring' : 'border-transparent bg-transparent hover:border-border-light'} mx-2 shadow-none gap-3 px-2 text-sm font-normal `}
          disabled={switching}
        >
          {switching ? <Loader2 className="size-3.5  animate-spin" /> : <Globe className="size-3.5" />}
          <span className="max-w-[150px] truncate ">{selectedOperator.name}</span>
          <ChevronsUpDown className={`h-3 w-3 ${isOperatorView ? 'text-accent-foreground' : 'text-accent-muted'} opacity-10`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px]  p-0 rounded-xl!">
        <Command>
          <CommandInput placeholder="Search operator..." />
          <CommandList>
            <CommandEmpty>No operator found.</CommandEmpty>
            <CommandGroup heading="Operators">
              {operators.map((operator) => (
                <CommandItem
                  key={operator.id}
                  onSelect={() => {
                    if (operator.id !== selectedOperator.id) {
                      handleOperatorSwitch(operator);
                    } else {
                      setOpen(false);
                    }
                  }}
                  className="text-sm"
                  disabled={switching}
                >
                  <div className="flex flex-col flex-1">
                    <span className="font-medium">{operator.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {operator.headquarters_country} • {operator.default_currency}
                    </span>
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0 text-accent-foreground',
                      selectedOperator.id === operator.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
